import { useEffect, useState, useMemo } from "react";
import { api, idr, idrShort, fmtNum } from "../lib/api";
import { Plus, Trash2, Package, X, Pencil, TrendingDown, Users2, UserPlus, UserMinus, History, ArrowRight, LayoutGrid, User, Search, PackagePlus, Copy, Layers, QrCode, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import InputRupiah from "../components/InputRupiah";
import { DatePicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { Skeleton } from "@/components/ui/skeleton";
import { hariIniISO, formatTanggal } from "@/lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

const blankItem = () => ({
  kode: "", nama: "", serial_no: "", imei: "", merk: "", kategori: "Elektronik",
  tgl_serah_terima: "", kondisi: "Baik", nilai: 0,
  umur_manfaat_bulan: 24, nilai_residu: 0,
  metode_penyusutan: "Garis Lurus", catatan: "",
});

const emptyForm = {
  employee_id: "", kode: "", nama: "", serial_no: "", imei: "", merk: "", kategori: "Elektronik",
  tgl_serah_terima: "", kondisi: "Baik", nilai: 0,
  umur_manfaat_bulan: 24, nilai_residu: 0,
  metode_penyusutan: "Garis Lurus", catatan: "",
};

export default function Assets() {
  const { user, can, branding } = useAuth();
  // Nama aplikasi dari pengaturan Branding (keputusan owner 24 Sep 2026, white-label).
  const appName = branding?.app_name || "EppleHub Operational System";
  const canCreate = can("assets", "create");
  const canUpdate = can("assets", "update");
  const canDelete = can("assets", "delete");
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  // Multi-item batch mode (only for CREATE)
  const [batchMode, setBatchMode] = useState(false);
  const [batchItems, setBatchItems] = useState([blankItem()]);
  const [batchEmp, setBatchEmp] = useState(""); // shared primary holder
  const [holderModal, setHolderModal] = useState(null); // { asset }
  const [newHolderEmp, setNewHolderEmp] = useState("");
  const [holderTab, setHolderTab] = useState("manage"); // manage | history
  const [historyRows, setHistoryRows] = useState([]);
  // Per-Karyawan view
  const [viewMode, setViewMode] = useState("assets"); // "assets" | "employee"
  const [selectedEmp, setSelectedEmp] = useState("");
  const [empSearch, setEmpSearch] = useState("");
  const [assignModal, setAssignModal] = useState(null); // { employee_id, employee_nama }
  const [assignSearch, setAssignSearch] = useState("");
  const [bulkSelected, setBulkSelected] = useState(() => new Set()); // Set of asset IDs
  const [bulkMode, setBulkMode] = useState("additional"); // "primary" | "additional"
  const [qrModal, setQrModal] = useState(null); // { asset, blobUrl }
  const [qrLabelSize, setQrLabelSize] = useState("medium");
  const [qrColumns, setQrColumns] = useState(3);
  const [qrPrinting, setQrPrinting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);   // lihat aset yang sudah dinonaktifkan

  const toggleBulk = (aid) => setBulkSelected(prev => {
    const n = new Set(prev);
    n.has(aid) ? n.delete(aid) : n.add(aid);
    return n;
  });
  const clearBulk = () => setBulkSelected(new Set());
  const selectAllVisible = (visible) => setBulkSelected(new Set(visible.map(a => a.id)));

  const submitBulk = async () => {
    if (bulkSelected.size === 0) { toast.error("Pilih minimal 1 aset dulu 😊"); return; }
    try {
      const { data } = await api.post("/assets/bulk-assign", {
        employee_id: assignModal.employee_id,
        asset_ids: Array.from(bulkSelected),
        mode: bulkMode,
      });
      if (data.success_count > 0) toast.success(data.message);
      if (data.skipped_count > 0) toast.warning(`${data.skipped_count} aset dilewati (sudah tercantum)`);
      clearBulk();
      setAssignModal(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal bulk assign"); }
  };

  const load = () => {
    // include_inactive: sejak hapus aset jadi soft-delete (riwayat serah-terima
    // tidak boleh ikut musnah), aset nonaktif disembunyikan backend secara
    // default. Tanpa saklar ini aset yang dinonaktifkan hilang dari layar dan
    // tidak ada cara mengaktifkannya kembali.
    api.get("/assets", { params: showInactive ? { include_inactive: true } : {} })
      .then(r => setItems(r.data));
    api.get("/employees").then(r => setEmps(r.data));
  };
  useEffect(() => { load(); }, [showInactive]);   // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setForm(emptyForm);
    setEditing(null);
    setBatchMode(true);
    setBatchItems([blankItem()]);
    setBatchEmp("");
    setModal(true);
  };
  const openEdit = (a) => {
    setForm({
      employee_id: a.employee_id || "", kode: a.kode || "", nama: a.nama || "",
      serial_no: a.serial_no || a.serial || "", imei: a.imei || "",
      merk: a.merk || "", kategori: a.kategori || "Elektronik",
      tgl_serah_terima: a.tgl_serah_terima || "",
      kondisi: a.kondisi || "Baik", nilai: a.nilai || 0,
      umur_manfaat_bulan: a.umur_manfaat_bulan || 24,
      nilai_residu: a.nilai_residu || 0,
      metode_penyusutan: a.metode_penyusutan || "Garis Lurus",
      catatan: a.catatan || "",
    });
    setEditing(a.id);
    setBatchMode(false);
    setModal(true);
  };

  // Batch row helpers
  const addBatchRow = () => setBatchItems(prev => [...prev, blankItem()]);
  const removeBatchRow = (idx) => setBatchItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  const updBatchRow = (idx, patch) => setBatchItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const duplicateBatchRow = (idx) => setBatchItems(prev => {
    const src = prev[idx]; const copy = { ...src, nama: src.nama + " (copy)", serial_no: "", imei: "" };
    return [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)];
  });
  const propagateFromFirst = (field) => {
    const val = batchItems[0]?.[field];
    setBatchItems(prev => prev.map((it, i) => i === 0 ? it : { ...it, [field]: val }));
    toast.success(`Nilai "${field}" disalin ke ${batchItems.length - 1} baris lain`);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/assets/${editing}`, form);
        toast.success("Aset diperbarui");
        setModal(false); setForm(emptyForm); setEditing(null); load();
        return;
      }
      // BATCH create
      const valid = batchItems.filter(it => (it.nama || "").trim());
      if (valid.length === 0) { toast.error("Minimal 1 item dengan nama inventaris terisi"); return; }
      const { data } = await api.post("/assets/bulk", {
        employee_id: batchEmp || null,
        items: valid,
      });
      toast.success(data.message || `Berhasil membuat ${data.created_count} aset`);
      if ((data.errors || []).length > 0) {
        data.errors.slice(0, 5).forEach(e => toast.warning(e));
      }
      setModal(false); setBatchItems([blankItem()]); setBatchEmp(""); setBatchMode(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };

  // Modal ini dipakai dua mode (edit satu aset / batch-create banyak baris
  // sekaligus, keduanya menyentuh nilai perolehan Rp) dan JSX-nya ada di
  // komponen halaman yang tidak unmount — `active` mengikuti `modal` supaya
  // dirty-state di-reset tiap kali modal dibuka lagi.
  const assetGuard = useModalGuard({
    active: modal, dangerous: true,
    onClose: () => setModal(false), onSubmit: save,
  });
  // Urutan bawaan dipertahankan sampai header diklik.
  const { sortKey: assetSortKey, sortDir: assetSortDir, toggle: toggleAssetSort } = useSortState();

  const del = async (id) => {
    // Bukan hapus permanen lagi: backend menonaktifkan dan MEMPERTAHANKAN
    // riwayat serah-terimanya. Teksnya dibuat jujur supaya tidak ada yang
    // mengira datanya musnah (atau sebaliknya, mengira masih aktif).
    if (!confirm("Nonaktifkan aset ini?\n\nAset disembunyikan dari daftar, tapi riwayat serah-terimanya tetap tersimpan dan bisa diaktifkan kembali lewat saklar 'Tampilkan aset nonaktif'.")) return;
    try {
      const { data } = await api.delete(`/assets/${id}`);
      toast.success(data?.message || "Aset dinonaktifkan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menonaktifkan aset"); }
  };

  const restore = async (a) => {
    try {
      await api.patch(`/assets/${a.id}`, { is_active: true });
      toast.success(`Aset '${a.nama}' diaktifkan kembali`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengaktifkan aset"); }
  };

  // ---- Multi-holder ops ----
  const openHolderMgr = (asset) => { setHolderModal({ asset }); setNewHolderEmp(""); setHolderTab("manage"); setHistoryRows([]); };
  const loadHistory = async (aid) => {
    try {
      const { data } = await api.get(`/assets/${aid}/history`);
      setHistoryRows(data);
    } catch { toast.error("Gagal load history"); }
  };
  const addHolder = async () => {
    if (!newHolderEmp || !holderModal) return;
    try {
      const { data } = await api.post(`/assets/${holderModal.asset.id}/holders`, { employee_id: newHolderEmp });
      toast.success("Pemegang ditambahkan");
      setHolderModal({ asset: data });
      setNewHolderEmp("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const removeHolder = async (empId) => {
    if (!confirm("Hapus pemegang tambahan?")) return;
    try {
      const { data } = await api.delete(`/assets/${holderModal.asset.id}/holders/${empId}`);
      toast.success("Pemegang dihapus");
      setHolderModal({ asset: data });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const totals = useMemo(() => ({
    nilai_awal: items.reduce((a, x) => a + (x.nilai || 0), 0),
    nilai_sekarang: items.reduce((a, x) => a + (x.nilai_saat_ini || 0), 0),
    depresiasi_akumulasi: items.reduce((a, x) => a + (x.akumulasi_depresiasi || 0), 0),
  }), [items]);

  // Group assets by primary holder for a quick "aset yang dipegang karyawan X" view
  const byEmployee = useMemo(() => {
    const map = {};
    items.forEach(a => {
      const holders = [
        ...(a.employee_id ? [{ id: a.employee_id, nama: a.employee_nama || "-", primary: true }] : []),
        ...((a.additional_holders || []).map(h => ({ id: h.employee_id, nama: h.employee_nama || "-", primary: false }))),
      ];
      holders.forEach(h => {
        map[h.id] = map[h.id] || { nama: h.nama, assets: [] };
        map[h.id].assets.push({ ...a, _held_as: h.primary ? "primary" : "additional" });
      });
    });
    return Object.entries(map).map(([id, v]) => ({ id, ...v })).sort((a, b) => b.assets.length - a.assets.length);
  }, [items]);

  // Employee-centric current selection
  const currentEmpAssets = useMemo(() => {
    if (!selectedEmp) return [];
    const row = byEmployee.find(e => e.id === selectedEmp);
    return row ? row.assets : [];
  }, [selectedEmp, byEmployee]);

  const filteredEmps = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return emps;
    return emps.filter(e => (e.nama || "").toLowerCase().includes(q) || (e.branch || "").toLowerCase().includes(q));
  }, [empSearch, emps]);

  // Assets available to assign to a target employee (excludes ones this employee already holds)
  const availableAssetsForAssign = useMemo(() => {
    if (!assignModal) return [];
    const empId = assignModal.employee_id;
    const q = assignSearch.trim().toLowerCase();
    return items.filter(a => {
      const isPrimary = a.employee_id === empId;
      const isAdditional = (a.additional_holders || []).some(h => h.employee_id === empId);
      if (isPrimary || isAdditional) return false;
      if (!q) return true;
      return (a.nama || "").toLowerCase().includes(q) || (a.kode || "").toLowerCase().includes(q) || (a.serial_no || a.serial || "").toLowerCase().includes(q) || (a.imei || "").toLowerCase().includes(q);
    });
  }, [assignModal, items, assignSearch]);

  const openAssignModal = () => {
    if (!selectedEmp) return;
    const empRow = emps.find(e => e.id === selectedEmp);
    setAssignModal({ employee_id: selectedEmp, employee_nama: empRow?.nama || "-" });
    setAssignSearch("");
  };

  const assignExisting = async (asset, mode) => {
    try {
      if (mode === "primary") {
        await api.patch(`/assets/${asset.id}`, { employee_id: assignModal.employee_id });
        toast.success(`Ditugaskan sebagai pemegang utama · ${asset.nama}`);
      } else {
        await api.post(`/assets/${asset.id}/holders`, { employee_id: assignModal.employee_id });
        toast.success(`Ditambahkan sebagai pemegang tambahan · ${asset.nama}`);
      }
      setAssignModal(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const openNewForEmployee = () => {
    if (!selectedEmp) return;
    setForm(emptyForm);
    setEditing(null);
    setBatchMode(true);
    setBatchItems([blankItem()]);
    setBatchEmp(selectedEmp);
    setModal(true);
    setAssignModal(null);
  };

  // ---- QR helpers ----
  const openQrModal = async (asset) => {
    setQrModal({ asset, blobUrl: null });
    try {
      const resp = await api.get(`/assets/${asset.id}/qr.png`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(resp.data);
      setQrModal({ asset, blobUrl });
    } catch { toast.error("Gagal muat QR"); }
  };
  const closeQrModal = () => {
    if (qrModal?.blobUrl) URL.revokeObjectURL(qrModal.blobUrl);
    setQrModal(null);
  };
  const downloadQrPng = async (asset) => {
    try {
      const resp = await api.get(`/assets/${asset.id}/qr.png`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `qr_${asset.kode || asset.id.slice(0, 8)}_${asset.nama.replace(/\s+/g, "_")}.png`; a.click();
      URL.revokeObjectURL(url);
      toast.success("QR PNG diunduh");
    } catch { toast.error("Gagal unduh QR"); }
  };
  const printBulkQrLabels = async (assetIds) => {
    if (!assetIds || assetIds.length === 0) { toast.error("Pilih minimal 1 aset"); return; }
    setQrPrinting(true);
    try {
      const resp = await api.post("/assets/qr-labels-pdf", {
        asset_ids: assetIds, label_size: qrLabelSize, columns: qrColumns,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `asset_qr_labels_${hariIniISO()}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`PDF ${assetIds.length} label QR diunduh — siap di-print`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal generate PDF"); }
    finally { setQrPrinting(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-start justify-between mb-6 md:mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Aset</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Aset Karyawan &amp; Penyusutan</h1>
          <p className="mt-2 text-sm text-slate-500">
            Daftar aset perusahaan · nilai auto-menyusut per bulan · <b>1 aset bisa dipegang 1+ karyawan</b>.
          </p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <button onClick={() => printBulkQrLabels(items.map(a => a.id))}
              disabled={qrPrinting || items.length === 0}
              data-testid="btn-print-all-qr"
              title="Print label QR untuk semua aset"
              className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium disabled:opacity-50">
              <Printer className="w-4 h-4" /> {qrPrinting ? "Membuat PDF..." : "Print QR Semua"}
            </button>
            <button onClick={openNew} data-testid="btn-add-asset"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">
              <Plus className="w-4 h-4" /> Tambah Aset
            </button>
          </div>
        )}
      </header>

      {/* View Mode Toggle */}
      <div className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-lg p-1 mb-5" data-testid="asset-view-toggle">
        <button onClick={() => setViewMode("assets")} data-testid="btn-view-assets"
          className={`inline-flex items-center gap-1.5 px-3.5 h-8 rounded-md text-xs font-semibold transition-colors ${
            viewMode === "assets" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}>
          <LayoutGrid className="w-3.5 h-3.5" /> Per Aset
        </button>
        <button onClick={() => setViewMode("employee")} data-testid="btn-view-employee"
          className={`inline-flex items-center gap-1.5 px-3.5 h-8 rounded-md text-xs font-semibold transition-colors ${
            viewMode === "employee" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}>
          <User className="w-3.5 h-3.5" /> Per Karyawan
        </button>
      </div>

      {/* Summary */}
      {viewMode === "assets" && (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-5">
        <SumCard label="Total Aset" value={fmtNum(items.length)} sub="unit" tint="slate" />
        <SumCard label="Nilai Awal" value={idr(totals.nilai_awal)} sub="perolehan" tint="emerald" />
        <SumCard label="Nilai Buku Sekarang" value={idr(totals.nilai_sekarang)}
          sub={`Depr. ${idr(totals.depresiasi_akumulasi)}`} tint="amber" />
        <SumCard label="Karyawan Pegang" value={fmtNum(byEmployee.length)} sub="orang" tint="violet" />
      </div>

      {/* By-employee summary — quick view "siapa pegang apa" */}
      {byEmployee.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
            <Users2 className="w-3.5 h-3.5" /> Aset yang Dipegang Karyawan (Top 6)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {byEmployee.slice(0, 6).map(e => (
              <div key={e.id} data-testid={`byemp-${e.id}`}
                className="border border-slate-200 rounded-lg p-3 hover:border-emerald-400 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <p className="font-medium text-slate-900 text-sm truncate">{e.nama}</p>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                    {e.assets.length} aset
                  </span>
                </div>
                <div className="space-y-0.5">
                  {e.assets.slice(0, 4).map(a => (
                    <p key={a.id} className="text-[11px] text-slate-600 truncate">
                      · {a.nama} <span className="text-slate-400 font-mono">{a.kode}</span>
                      {a._held_as === "additional" && <span className="ml-1 text-violet-600">(tambahan)</span>}
                    </p>
                  ))}
                  {e.assets.length > 4 && <p className="text-[10px] text-slate-400 italic">+{e.assets.length - 4} lagi</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end mb-2">
        <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
            data-testid="toggle-aset-nonaktif" className="w-3.5 h-3.5 accent-slate-600" />
          Tampilkan aset nonaktif
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]" data-testid="assets-table">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortTh label="Kode" sortKeyName="kode" sortKey={assetSortKey} sortDir={assetSortDir} onSort={toggleAssetSort} testid="asset-sort-kode" />
              <SortTh label="Nama Inventaris" sortKeyName="nama" sortKey={assetSortKey} sortDir={assetSortDir} onSort={toggleAssetSort} testid="asset-sort-nama" />
              <SortTh label="Karyawan Pemegang" sortKeyName="employee_nama" sortKey={assetSortKey} sortDir={assetSortDir} onSort={toggleAssetSort} testid="asset-sort-pemegang" />
              <SortTh label="Tgl Serah" sortKeyName="tgl_serah_terima" sortKey={assetSortKey} sortDir={assetSortDir} onSort={toggleAssetSort} testid="asset-sort-tgl" />
              <SortTh label="Nilai Awal" sortKeyName="nilai" sortKey={assetSortKey} sortDir={assetSortDir} onSort={toggleAssetSort} align="right" testid="asset-sort-nilai" />
              <th className="text-center py-3 px-4">Umur</th>
              <SortTh label="Nilai Sekarang" sortKeyName="nilai_saat_ini" sortKey={assetSortKey} sortDir={assetSortDir} onSort={toggleAssetSort} align="right" testid="asset-sort-sekarang" />
              <th className="text-right py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {sortRows(items, assetSortKey, assetSortDir).map(a => {
              const extras = a.additional_holders || [];
              return (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`asset-row-${a.id}`}>
                  <td className="py-3 px-4 font-mono text-xs">{a.kode}</td>
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-900">
                      {a.nama}
                      {a.is_active === false && (
                        <span className="ml-2 px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold align-middle">
                          NONAKTIF
                        </span>
                      )}
                    </p>
                    {(a.serial_no || a.serial) && <p className="text-[11px] text-slate-500 font-mono">S/N: {a.serial_no || a.serial}</p>}
                    {a.imei && <p className="text-[11px] text-slate-500 font-mono">IMEI: {a.imei}</p>}
                    {a.merk && <p className="text-[10px] text-slate-400">{a.merk} · {a.kategori || "-"}</p>}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-col gap-0.5">
                      {a.employee_nama && (
                        <span className="text-slate-700 text-sm inline-flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {a.employee_nama}
                        </span>
                      )}
                      {extras.map((h, i) => (
                        <span key={i} className="text-slate-600 text-xs inline-flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-violet-400" /> {h.employee_nama || h.employee_id}
                        </span>
                      ))}
                      {!a.employee_nama && extras.length === 0 && <span className="text-slate-400 italic text-xs">— tidak ditugaskan —</span>}
                      {canUpdate && (
                        <button onClick={() => openHolderMgr(a)} data-testid={`btn-holders-${a.id}`}
                          className="mt-1 text-[10px] text-violet-700 hover:text-violet-900 font-medium inline-flex items-center gap-1 self-start">
                          <UserPlus className="w-3 h-3" /> Kelola pemegang{extras.length > 0 && ` (+${extras.length})`}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-600 text-xs">
                    {a.tgl_serah_terima || "-"}
                    {a.months_elapsed > 0 && <p className="text-[10px] text-slate-400">{a.months_elapsed} bln lalu</p>}
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-xs">{idr(a.nilai)}</td>
                  <td className="py-3 px-4 text-center text-xs">
                    {a.metode_penyusutan === "Non-penyusutan" ? (
                      <span className="text-slate-400">-</span>
                    ) : (
                      <div>
                        <span className="font-medium text-slate-700">{a.umur_manfaat_bulan}bln</span>
                        {a.depresiasi_per_bulan > 0 && (
                          <p className="text-[10px] text-rose-500">-{idrShort(a.depresiasi_per_bulan)}/bln</p>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <p className="font-mono font-semibold text-slate-900">{idr(a.nilai_saat_ini)}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      a.sisa_pct >= 75 ? "bg-emerald-100 text-emerald-800" :
                      a.sisa_pct >= 40 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"
                    }`}>{a.sisa_pct}%</span>
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button onClick={() => openQrModal(a)} data-testid={`btn-qr-asset-${a.id}`}
                      title="Lihat / download QR aset"
                      className="p-2 hover:bg-emerald-50 rounded-lg text-slate-400 hover:text-emerald-700">
                      <QrCode className="w-4 h-4" />
                    </button>
                    {canUpdate && (
                      <button onClick={() => openEdit(a)} data-testid={`btn-edit-asset-${a.id}`}
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {/* Aset nonaktif: tombol hapus diganti tombol aktifkan kembali,
                        karena menonaktifkan yang sudah nonaktif ditolak backend. */}
                    {canDelete && a.is_active === false && (
                      <button onClick={() => restore(a)} data-testid={`btn-restore-asset-${a.id}`}
                        title="Aktifkan kembali aset ini"
                        className="p-2 hover:bg-emerald-50 rounded-lg text-slate-400 hover:text-emerald-700">
                        <History className="w-4 h-4" />
                      </button>
                    )}
                    {canDelete && a.is_active !== false && (
                      <button onClick={() => del(a.id)} data-testid={`btn-del-asset-${a.id}`}
                        title="Nonaktifkan aset (riwayat tetap tersimpan)"
                        className="p-2 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-sm text-slate-500">
                <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />Belum ada aset
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}

      {/* ===== PER KARYAWAN VIEW ===== */}
      {viewMode === "employee" && (
        <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-4" data-testid="view-per-karyawan">
          {/* Employee list sidebar */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="p-3 border-b border-slate-200 bg-slate-50">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={empSearch} onChange={e => setEmpSearch(e.target.value)}
                  data-testid="emp-search"
                  placeholder="Cari karyawan..."
                  className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-xs" />
              </div>
              <p className="text-[10px] text-slate-500 mt-2">
                <b>{byEmployee.length}</b> karyawan pegang aset · <b>{emps.length - byEmployee.length}</b> belum pegang
              </p>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {filteredEmps.map(e => {
                const row = byEmployee.find(be => be.id === e.id);
                const count = row ? row.assets.length : 0;
                const isSelected = selectedEmp === e.id;
                return (
                  <button key={e.id} onClick={() => setSelectedEmp(e.id)}
                    data-testid={`emp-select-${e.id}`}
                    className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-emerald-50 transition-colors flex items-center justify-between gap-2 ${
                      isSelected ? "bg-emerald-50 border-l-4 border-l-emerald-500" : ""
                    }`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{e.nama}</p>
                      <p className="text-[10px] text-slate-500 truncate">{e.branch || "-"} · {e.grade_jabatan || "-"}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0 ${
                      count > 0 ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
                    }`}>
                      {count} aset
                    </span>
                  </button>
                );
              })}
              {filteredEmps.length === 0 && (
                <p className="text-xs text-slate-400 italic text-center py-6">Tidak ada karyawan cocok</p>
              )}
            </div>
          </div>

          {/* Detail panel */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            {!selectedEmp && (
              <div className="p-10 text-center">
                <User className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Pilih karyawan di kiri untuk melihat aset yang dipegang</p>
                <p className="text-xs text-slate-400 mt-1">1 karyawan bisa memegang <b>banyak aset</b> sekaligus (utama + tambahan)</p>
              </div>
            )}
            {selectedEmp && (() => {
              const empRow = emps.find(e => e.id === selectedEmp);
              return (
                <div>
                  <div className="p-5 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Karyawan Terpilih</p>
                      <h2 className="font-display text-lg font-bold text-slate-900 mt-1">{empRow?.nama || "-"}</h2>
                      <p className="text-xs text-slate-600">
                        {empRow?.grade_jabatan || "-"} · {empRow?.branch || "-"} · {currentEmpAssets.length} aset dipegang
                      </p>
                    </div>
                    {canUpdate && (
                      <button onClick={openAssignModal} data-testid="btn-open-assign-existing"
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium">
                        <PackagePlus className="w-4 h-4" /> Tambah Aset ke Karyawan
                      </button>
                    )}
                  </div>

                  {currentEmpAssets.length === 0 ? (
                    <div className="p-10 text-center">
                      <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">Karyawan ini belum memegang aset apa pun</p>
                      {canUpdate && (
                        <button onClick={openAssignModal} data-testid="btn-add-first-asset"
                          className="mt-3 inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium">
                          <Plus className="w-3.5 h-3.5" /> Tambah aset pertama
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            <th className="text-left py-3 px-4">Kode</th>
                            <th className="text-left py-3 px-4">Nama Inventaris</th>
                            <th className="text-center py-3 px-4">Status</th>
                            <th className="text-left py-3 px-4">Tgl Serah</th>
                            <th className="text-right py-3 px-4">Nilai Sekarang</th>
                            <th className="text-right py-3 px-4"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentEmpAssets.map(a => (
                            <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`emp-asset-${a.id}`}>
                              <td className="py-3 px-4 font-mono text-xs">{a.kode}</td>
                              <td className="py-3 px-4">
                                <p className="font-medium text-slate-900">{a.nama}</p>
                                {(a.serial_no || a.serial) && <p className="text-[11px] text-slate-500 font-mono">S/N: {a.serial_no || a.serial}</p>}
                                {a.imei && <p className="text-[11px] text-slate-500 font-mono">IMEI: {a.imei}</p>}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  a._held_as === "primary" ? "bg-emerald-100 text-emerald-800" : "bg-violet-100 text-violet-800"
                                }`}>
                                  {a._held_as === "primary" ? "Pemegang Utama" : "Pemegang Tambahan"}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-slate-600 text-xs">{a.tgl_serah_terima || "-"}</td>
                              <td className="py-3 px-4 text-right">
                                <p className="font-mono font-semibold text-slate-900">{idr(a.nilai_saat_ini)}</p>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                  a.sisa_pct >= 75 ? "bg-emerald-100 text-emerald-800" :
                                  a.sisa_pct >= 40 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"
                                }`}>{a.sisa_pct}%</span>
                              </td>
                              <td className="py-3 px-4 text-right whitespace-nowrap">
                                {canUpdate && (
                                  <button onClick={() => openHolderMgr(a)}
                                    data-testid={`btn-emp-holders-${a.id}`}
                                    title="Kelola pemegang aset"
                                    className="p-2 hover:bg-violet-50 rounded-lg text-slate-400 hover:text-violet-700">
                                    <Users2 className="w-4 h-4" />
                                  </button>
                                )}
                                {canUpdate && (
                                  <button onClick={() => openEdit(a)}
                                    data-testid={`btn-emp-edit-${a.id}`}
                                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600">
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                )}
                                {canUpdate && a._held_as === "additional" && (
                                  <button onClick={async () => {
                                      if (!confirm("Lepas karyawan ini dari daftar pemegang tambahan?")) return;
                                      try {
                                        await api.delete(`/assets/${a.id}/holders/${selectedEmp}`);
                                        toast.success("Karyawan dilepas dari aset ini");
                                        load();
                                      } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
                                    }}
                                    data-testid={`btn-emp-remove-holder-${a.id}`}
                                    title="Lepas pemegang tambahan"
                                    className="p-2 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600">
                                    <UserMinus className="w-4 h-4" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Assign Existing Asset Modal */}
      {assignModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setAssignModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="assign-asset-modal">
            <div className="bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2">
                <PackagePlus className="w-5 h-5" />
                <div>
                  <h3 className="font-display font-semibold text-sm">Tambah Aset ke Karyawan</h3>
                  <p className="text-[11px] text-violet-100">Target: <b>{assignModal.employee_nama}</b></p>
                </div>
              </div>
              <button onClick={() => setAssignModal(null)}
                className="p-1.5 hover:bg-white/20 rounded-lg"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-4 border-b border-slate-100 bg-slate-50">
              <button onClick={openNewForEmployee} data-testid="btn-assign-new-asset"
                className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                <Plus className="w-4 h-4" /> Buat Aset Baru untuk Karyawan Ini
              </button>
              <p className="text-[10px] text-slate-500 text-center mt-2">Atau pilih dari aset existing di bawah:</p>
            </div>

            <div className="p-4">
              <div className="relative mb-3">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={assignSearch} onChange={e => setAssignSearch(e.target.value)}
                  data-testid="assign-search"
                  placeholder="Cari nama, kode, atau serial..."
                  className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-xs" />
              </div>
              {/* Bulk select toolbar */}
              <div className="flex items-center justify-between mb-3 text-[11px]">
                <button onClick={() => bulkSelected.size === availableAssetsForAssign.length ? clearBulk() : selectAllVisible(availableAssetsForAssign)}
                  data-testid="btn-bulk-select-all"
                  className="text-violet-600 hover:text-violet-800 font-medium">
                  {bulkSelected.size === availableAssetsForAssign.length && availableAssetsForAssign.length > 0 ? "Batal Pilih Semua" : "✓ Pilih Semua"}
                </button>
                {bulkSelected.size > 0 && (
                  <span className="text-slate-600" data-testid="bulk-selected-count">
                    <b>{bulkSelected.size}</b> dipilih
                  </span>
                )}
              </div>
              <div className="max-h-[340px] overflow-y-auto space-y-1.5">
                {availableAssetsForAssign.length === 0 && (
                  <p className="text-xs text-slate-400 italic text-center py-6">
                    {assignSearch ? "Tidak ada aset cocok" : "Semua aset sudah dipegang karyawan ini atau belum ada aset lain di sistem"}
                  </p>
                )}
                {availableAssetsForAssign.map(a => {
                  const hasPrimary = !!a.employee_id;
                  const isChecked = bulkSelected.has(a.id);
                  return (
                    <div key={a.id} data-testid={`assign-row-${a.id}`}
                      className={`border rounded-lg p-2.5 flex items-center justify-between gap-3 transition-colors ${
                        isChecked ? "border-violet-400 bg-violet-50" : "border-slate-200 hover:border-violet-300"
                      }`}>
                      <input type="checkbox" checked={isChecked} onChange={() => toggleBulk(a.id)}
                        data-testid={`bulk-check-${a.id}`}
                        className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">{a.nama} <span className="font-mono text-[10px] text-slate-400 ml-1">{a.kode}</span></p>
                        <p className="text-[11px] text-slate-500">
                          {hasPrimary ? <>Utama: <b>{a.employee_nama}</b></> : <span className="italic">Belum ada pemegang utama</span>}
                          {(a.additional_holders || []).length > 0 && ` · +${a.additional_holders.length} tambahan`}
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {!hasPrimary && (
                          <button onClick={() => assignExisting(a, "primary")}
                            data-testid={`btn-assign-primary-${a.id}`}
                            title="Jadikan pemegang utama"
                            className="text-[10px] px-2 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium">
                            Jadikan Utama
                          </button>
                        )}
                        <button onClick={() => assignExisting(a, "additional")}
                          data-testid={`btn-assign-additional-${a.id}`}
                          title="Tambah sebagai pemegang tambahan"
                          className="text-[10px] px-2 py-1.5 rounded bg-violet-600 hover:bg-violet-700 text-white font-medium">
                          + Tambahan
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Sticky bottom bulk action bar */}
              {bulkSelected.size > 0 && (
                <div className="sticky bottom-0 mt-3 pt-3 border-t border-slate-200 bg-white" data-testid="bulk-action-bar">
                  <p className="text-[11px] font-semibold text-slate-700 mb-2">
                    Assign <b>{bulkSelected.size}</b> aset ke <b className="text-violet-700">{assignModal.employee_nama}</b> sebagai:
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => { setBulkMode("primary"); submitBulk(); }}
                      data-testid="btn-bulk-assign-primary"
                      className="flex-1 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold">
                      Pemegang Utama
                    </button>
                    <button onClick={() => { setBulkMode("additional"); submitBulk(); }}
                      data-testid="btn-bulk-assign-additional"
                      className="flex-1 h-9 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold">
                      Pemegang Tambahan
                    </button>
                    <button onClick={clearBulk} data-testid="btn-bulk-clear"
                      className="h-9 px-3 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">
                      Batal
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Holder Manager Modal */}
      {holderModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setHolderModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="holder-modal">
            <div className="bg-gradient-to-r from-violet-600 to-violet-500 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2">
                <Users2 className="w-5 h-5" />
                <div>
                  <h3 className="font-display font-semibold text-sm">Kelola Pemegang Aset</h3>
                  <p className="text-[11px] text-violet-100">{holderModal.asset.nama} · <span className="font-mono">{holderModal.asset.kode}</span></p>
                </div>
              </div>
              <button onClick={() => setHolderModal(null)}
                className="p-1.5 hover:bg-white/20 rounded-lg"><X className="w-4 h-4" /></button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-200 bg-slate-50">
              <button onClick={() => setHolderTab("manage")}
                data-testid="tab-manage"
                className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                  holderTab === "manage" ? "border-violet-600 text-violet-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}>
                <Users2 className="w-3.5 h-3.5 inline mb-0.5 mr-1" /> Pemegang
              </button>
              <button onClick={() => { setHolderTab("history"); loadHistory(holderModal.asset.id); }}
                data-testid="tab-history"
                className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                  holderTab === "history" ? "border-violet-600 text-violet-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}>
                <History className="w-3.5 h-3.5 inline mb-0.5 mr-1" /> Riwayat Perpindahan
              </button>
            </div>

            {holderTab === "manage" ? (
            <div className="p-5 space-y-4">
              {/* Primary */}
              {holderModal.asset.employee_id && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Pemegang Utama</p>
                    <p className="font-medium text-slate-900">{holderModal.asset.employee_nama || "-"}</p>
                  </div>
                  <span className="text-[10px] text-emerald-700 italic">edit via form aset</span>
                </div>
              )}

              {/* Additional list */}
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-2">
                  Pemegang Tambahan ({(holderModal.asset.additional_holders || []).length})
                </p>
                <div className="space-y-2">
                  {(holderModal.asset.additional_holders || []).map((h, i) => (
                    <div key={i} className="bg-violet-50 border border-violet-200 rounded-lg p-2.5 flex items-center justify-between"
                      data-testid={`holder-row-${h.employee_id}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-violet-200 text-violet-800 grid place-items-center text-xs font-bold">
                          {(h.employee_nama || "?")[0]}
                        </span>
                        <span className="text-sm text-slate-900">{h.employee_nama || h.employee_id}</span>
                      </div>
                      <button onClick={() => removeHolder(h.employee_id)} data-testid={`btn-remove-holder-${h.employee_id}`}
                        className="p-1.5 hover:bg-rose-100 rounded text-slate-500 hover:text-rose-600">
                        <UserMinus className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {(holderModal.asset.additional_holders || []).length === 0 && (
                    <p className="text-xs text-slate-400 italic text-center py-3">Belum ada pemegang tambahan</p>
                  )}
                </div>
              </div>

              {/* Add form */}
              {canUpdate && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-slate-600 mb-2">Tambah Pemegang Baru</p>
                  <div className="flex gap-2">
                    <select value={newHolderEmp} onChange={e => setNewHolderEmp(e.target.value)}
                      data-testid="holder-new-select"
                      className="flex-1 h-9 px-3 rounded border border-slate-200 text-sm bg-white">
                      <option value="">— pilih karyawan —</option>
                      {emps.map(e => <option key={e.id} value={e.id}>{e.nama}{e.branch ? ` · ${e.branch}` : ""}</option>)}
                    </select>
                    <button onClick={addHolder} disabled={!newHolderEmp} data-testid="btn-add-holder"
                      className="inline-flex items-center gap-1 h-9 px-3 rounded bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium disabled:opacity-50">
                      <UserPlus className="w-3.5 h-3.5" /> Tambah
                    </button>
                  </div>
                </div>
              )}
            </div>
            ) : (
              <div className="p-5 max-h-[500px] overflow-y-auto">
                {historyRows.length === 0 && (
                  <p className="text-xs text-slate-400 italic text-center py-8">Belum ada riwayat perpindahan aset ini.</p>
                )}
                <div className="space-y-2">
                  {historyRows.map(h => {
                    const ACTION_META = {
                      create:            { label: "Aset dibuat",           color: "emerald", icon: "🆕" },
                      assign_primary:    { label: "Ditugaskan primary",    color: "emerald", icon: "👤" },
                      reassign_primary:  { label: "Pemegang utama pindah", color: "amber",   icon: "🔄" },
                      unassign_primary:  { label: "Pemegang utama dilepas", color: "slate",   icon: "❎" },
                      add_holder:        { label: "Tambah pemegang",        color: "violet",  icon: "➕" },
                      remove_holder:     { label: "Hapus pemegang",         color: "rose",    icon: "➖" },
                    };
                    const meta = ACTION_META[h.action] || { label: h.action, color: "slate", icon: "•" };
                    return (
                      <div key={h.id} data-testid={`history-row-${h.id}`}
                        className="border border-slate-200 rounded-lg p-3 hover:border-violet-300">
                        <div className="flex items-start gap-3">
                          <span className="text-lg">{meta.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold bg-${meta.color}-100 text-${meta.color}-800`}>
                                {meta.label}
                              </span>
                              <span className="text-[10px] text-slate-500 font-mono">
                                {formatTanggal(h.at, { denganJam: true })}
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-slate-700 flex items-center gap-1 flex-wrap">
                              {h.from_employee_nama && (
                                <><span className="font-medium">{h.from_employee_nama}</span> <ArrowRight className="w-3 h-3 text-slate-400 inline" /></>
                              )}
                              {h.to_employee_nama && <span className="font-medium">{h.to_employee_nama}</span>}
                              {!h.from_employee_nama && !h.to_employee_nama && <span className="text-slate-400 italic">(tanpa pemegang)</span>}
                            </div>
                            {h.note && <p className="text-[11px] text-slate-500 italic mt-0.5">&ldquo;{h.note}&rdquo;</p>}
                            <p className="text-[10px] text-slate-400 mt-0.5">oleh: {h.actor_email}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={assetGuard.requestClose}>
          <div ref={assetGuard.contentRef} className={`bg-white rounded-2xl w-full ${editing ? "max-w-lg" : "max-w-6xl"} my-8`} onClick={e => e.stopPropagation()}
            data-testid="asset-modal">
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-display font-semibold text-slate-900 inline-flex items-center gap-2">
                  {!editing && <Layers className="w-5 h-5 text-emerald-600" />}
                  {editing ? "Edit Aset" : `Tambah Aset (Batch · ${batchItems.length} item)`}
                </h3>
                {!editing && (
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Isi banyak inventaris sekaligus. Tiap baris = 1 aset dengan detail sendiri (nama, serial/IMEI, tanggal, nilai, penyusutan).
                  </p>
                )}
              </div>
              <button onClick={assetGuard.requestClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>

            {editing ? (
              /* -------- EDIT single asset -------- */
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="col-span-1 sm:col-span-2 block">
                  <span className="text-xs font-medium text-slate-600">Pemegang Utama</span>
                  <select value={form.employee_id} onChange={e => setForm(s => ({ ...s, employee_id: e.target.value }))}
                    data-testid="asset-employee-select"
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="">— tidak ditugaskan —</option>
                    {emps.map(e => <option key={e.id} value={e.id}>{e.nama}{e.branch ? ` · ${e.branch}` : ""}</option>)}
                  </select>
                </label>
                <F l="Kode Aset"><Inp v={form.kode} onC={v => setForm(s => ({ ...s, kode: v }))} tid="asset-kode" /></F>
                <F l="Nama Inventaris *"><Inp v={form.nama} onC={v => setForm(s => ({ ...s, nama: v }))} tid="asset-nama" /></F>
                <F l="Merk / Brand"><Inp v={form.merk} onC={v => setForm(s => ({ ...s, merk: v }))} tid="asset-merk" /></F>
                <F l="Kategori">
                  <select value={form.kategori} onChange={e => setForm(s => ({ ...s, kategori: e.target.value }))}
                    data-testid="asset-kategori" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option>Elektronik</option><option>Kendaraan</option><option>Furniture</option>
                    <option>Perkakas</option><option>Software / Lisensi</option><option>Lainnya</option>
                  </select>
                </F>
                <F l="Serial No"><Inp v={form.serial_no} onC={v => setForm(s => ({ ...s, serial_no: v }))} tid="asset-serial" /></F>
                <F l="IMEI"><Inp v={form.imei} onC={v => setForm(s => ({ ...s, imei: v }))} tid="asset-imei" /></F>
                <F l="Tanggal Penyerahan"><DatePicker value={form.tgl_serah_terima} onChange={v => setForm(s => ({ ...s, tgl_serah_terima: v }))} data-testid="asset-tgl" /></F>
                <F l="Nilai Perolehan (Rp) *">
                  <InputRupiah value={form.nilai} onChange={n => setForm(s => ({ ...s, nilai: n }))} testid="asset-nilai"
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-600/40" />
                </F>
                <F l="Kondisi">
                  <select value={form.kondisi} onChange={e => setForm(s => ({ ...s, kondisi: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option>Baik</option><option>Rusak Ringan</option><option>Rusak Berat</option>
                  </select>
                </F>
                <F l="Metode Penyusutan">
                  <select value={form.metode_penyusutan} onChange={e => setForm(s => ({ ...s, metode_penyusutan: e.target.value }))}
                    data-testid="asset-metode" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="Garis Lurus">Garis Lurus (auto berkurang %/bln)</option>
                    <option value="Non-penyusutan">Non-penyusutan (aset tetap)</option>
                  </select>
                </F>
                <F l="Umur Manfaat (bulan)">
                  <Inp type="number" v={form.umur_manfaat_bulan}
                    onC={v => setForm(s => ({ ...s, umur_manfaat_bulan: parseInt(v) || 0 }))} tid="asset-umur" />
                </F>
                <F l="Nilai Residu (opsional)">
                  <InputRupiah value={form.nilai_residu}
                    onChange={n => setForm(s => ({ ...s, nilai_residu: n }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-600/40" />
                </F>
                {form.metode_penyusutan === "Garis Lurus" && form.umur_manfaat_bulan > 0 && (
                  <div className="col-span-1 sm:col-span-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800 flex items-start gap-2">
                    <TrendingDown className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <p><b>Depresiasi otomatis:</b> {(100 / form.umur_manfaat_bulan).toFixed(2)}% per bulan
                        · {idr((form.nilai - form.nilai_residu) / form.umur_manfaat_bulan)}/bln</p>
                      <p className="text-emerald-700/80 mt-0.5">Setelah {form.umur_manfaat_bulan} bulan nilai buku = {idr(form.nilai_residu)}</p>
                    </div>
                  </div>
                )}
                <F l="Catatan" className="col-span-1 sm:col-span-2">
                  <Inp v={form.catatan} onC={v => setForm(s => ({ ...s, catatan: v }))} />
                </F>
                <div className="col-span-1 sm:col-span-2 flex justify-end gap-2">
                  <button onClick={assetGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                  <button onClick={save} data-testid="btn-save-asset"
                    className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">
                    Perbarui
                  </button>
                </div>
              </div>
            ) : (
              /* -------- BATCH CREATE mode -------- */
              <div className="p-5 space-y-4">
                {/* Shared holder */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1">
                      <UserPlus className="w-3.5 h-3.5 text-emerald-600" /> Pemegang Utama (untuk semua item)
                    </span>
                    <select value={batchEmp} onChange={e => setBatchEmp(e.target.value)}
                      data-testid="batch-employee-select"
                      className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                      <option value="">— tidak ditugaskan —</option>
                      {emps.map(e => <option key={e.id} value={e.id}>{e.nama}{e.branch ? ` · ${e.branch}` : ""}</option>)}
                    </select>
                    <p className="text-[10px] text-slate-500 mt-1">Berlaku untuk semua item. Bisa dilepas per-item nanti via ikon pemegang.</p>
                  </label>
                  <div className="flex flex-col justify-end">
                    <p className="text-[10px] text-slate-600 mb-1">Aksi Cepat</p>
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={() => propagateFromFirst("kategori")} data-testid="btn-copy-kategori"
                        className="text-[10px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 text-slate-700 inline-flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Kategori dari baris 1 ke semua
                      </button>
                      <button onClick={() => propagateFromFirst("tgl_serah_terima")} data-testid="btn-copy-tgl"
                        className="text-[10px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 text-slate-700 inline-flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Tanggal
                      </button>
                      <button onClick={() => propagateFromFirst("metode_penyusutan")} data-testid="btn-copy-metode"
                        className="text-[10px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 text-slate-700 inline-flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Metode
                      </button>
                      <button onClick={() => propagateFromFirst("umur_manfaat_bulan")} data-testid="btn-copy-umur"
                        className="text-[10px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 text-slate-700 inline-flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Umur
                      </button>
                    </div>
                  </div>
                </div>

                {/* Batch rows */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-3 py-2 flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-700">Daftar Item Inventaris ({batchItems.length})</p>
                    <button onClick={addBatchRow} data-testid="btn-add-batch-row"
                      className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium">
                      <Plus className="w-3 h-3" /> Tambah Baris
                    </button>
                  </div>
                  <div className="divide-y divide-slate-100 max-h-[52vh] overflow-y-auto">
                    {batchItems.map((it, idx) => (
                      <div key={idx} className="p-3 hover:bg-emerald-50/30" data-testid={`batch-row-${idx}`}>
                        <div className="flex items-start gap-2">
                          <span className="flex-shrink-0 mt-1 w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold grid place-items-center">
                            {idx + 1}
                          </span>
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
                            <label className="md:col-span-4 block">
                              <span className="text-[10px] font-medium text-slate-500">Nama Inventaris *</span>
                              <input value={it.nama} onChange={e => updBatchRow(idx, { nama: e.target.value })}
                                data-testid={`batch-nama-${idx}`}
                                placeholder="contoh: Laptop Asus VivoBook"
                                className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs" />
                            </label>
                            <label className="md:col-span-2 block">
                              <span className="text-[10px] font-medium text-slate-500">Merk / Brand</span>
                              <input value={it.merk} onChange={e => updBatchRow(idx, { merk: e.target.value })}
                                data-testid={`batch-merk-${idx}`}
                                placeholder="Asus"
                                className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs" />
                            </label>
                            <label className="md:col-span-2 block">
                              <span className="text-[10px] font-medium text-slate-500">Kategori</span>
                              <select value={it.kategori} onChange={e => updBatchRow(idx, { kategori: e.target.value })}
                                data-testid={`batch-kategori-${idx}`}
                                className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                                <option>Elektronik</option><option>Kendaraan</option><option>Furniture</option>
                                <option>Perkakas</option><option>Software / Lisensi</option><option>Lainnya</option>
                              </select>
                            </label>
                            <label className="md:col-span-2 block">
                              <span className="text-[10px] font-medium text-slate-500">Kode Aset</span>
                              <input value={it.kode} onChange={e => updBatchRow(idx, { kode: e.target.value })}
                                data-testid={`batch-kode-${idx}`}
                                placeholder="auto/opsional"
                                className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs" />
                            </label>
                            <label className="md:col-span-2 block">
                              <span className="text-[10px] font-medium text-slate-500">Tgl Perolehan</span>
                              <DatePicker value={it.tgl_serah_terima}
                                onChange={v => updBatchRow(idx, { tgl_serah_terima: v })}
                                data-testid={`batch-tgl-${idx}`}
                                className="mt-0.5" />
                            </label>

                            <label className="md:col-span-3 block">
                              <span className="text-[10px] font-medium text-slate-500">Serial No</span>
                              <input value={it.serial_no} onChange={e => updBatchRow(idx, { serial_no: e.target.value })}
                                data-testid={`batch-serial-${idx}`}
                                placeholder="S/N unit"
                                className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs font-mono" />
                            </label>
                            <label className="md:col-span-3 block">
                              <span className="text-[10px] font-medium text-slate-500">IMEI</span>
                              <input value={it.imei} onChange={e => updBatchRow(idx, { imei: e.target.value })}
                                data-testid={`batch-imei-${idx}`}
                                placeholder="IMEI / SN perangkat (opsional)"
                                className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs font-mono" />
                            </label>
                            <label className="md:col-span-3 block">
                              <span className="text-[10px] font-medium text-slate-500">Nilai Perolehan (Rp) *</span>
                              <InputRupiah value={it.nilai}
                                onChange={n => updBatchRow(idx, { nilai: n })}
                                testid={`batch-nilai-${idx}`}
                                className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono" />
                            </label>
                            <label className="md:col-span-3 block">
                              <span className="text-[10px] font-medium text-slate-500">Nilai Residu (Rp)</span>
                              <InputRupiah value={it.nilai_residu}
                                onChange={n => updBatchRow(idx, { nilai_residu: n })}
                                testid={`batch-residu-${idx}`}
                                className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono" />
                            </label>

                            <label className="md:col-span-4 block">
                              <span className="text-[10px] font-medium text-slate-500">Metode Penyusutan</span>
                              <select value={it.metode_penyusutan}
                                onChange={e => updBatchRow(idx, { metode_penyusutan: e.target.value })}
                                data-testid={`batch-metode-${idx}`}
                                className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                                <option value="Garis Lurus">Garis Lurus</option>
                                <option value="Non-penyusutan">Non-penyusutan</option>
                              </select>
                            </label>
                            <label className="md:col-span-3 block">
                              <span className="text-[10px] font-medium text-slate-500">Umur Manfaat (bulan)</span>
                              <input type="number" value={it.umur_manfaat_bulan}
                                onChange={e => updBatchRow(idx, { umur_manfaat_bulan: parseInt(e.target.value) || 0 })}
                                data-testid={`batch-umur-${idx}`}
                                className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs text-right font-mono" />
                            </label>
                            <label className="md:col-span-3 block">
                              <span className="text-[10px] font-medium text-slate-500">Kondisi</span>
                              <select value={it.kondisi} onChange={e => updBatchRow(idx, { kondisi: e.target.value })}
                                data-testid={`batch-kondisi-${idx}`}
                                className="mt-0.5 w-full h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                                <option>Baik</option><option>Rusak Ringan</option><option>Rusak Berat</option>
                              </select>
                            </label>
                            <div className="md:col-span-2 flex items-end gap-1 justify-end">
                              <button onClick={() => duplicateBatchRow(idx)} data-testid={`btn-dup-batch-${idx}`}
                                title="Duplikat baris ini"
                                className="h-9 w-9 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-700 inline-grid place-items-center">
                                <Copy className="w-4 h-4" />
                              </button>
                              {batchItems.length > 1 && (
                                <button onClick={() => removeBatchRow(idx)} data-testid={`btn-remove-batch-${idx}`}
                                  title="Hapus baris"
                                  className="h-9 w-9 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 inline-grid place-items-center">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            <label className="md:col-span-12 block">
                              <span className="text-[10px] font-medium text-slate-500">Catatan</span>
                              <input value={it.catatan} onChange={e => updBatchRow(idx, { catatan: e.target.value })}
                                data-testid={`batch-catatan-${idx}`}
                                className="mt-0.5 w-full h-8 px-2 rounded border border-slate-200 text-xs" />
                            </label>
                            {/* Depresiasi preview mini */}
                            {it.metode_penyusutan === "Garis Lurus" && it.umur_manfaat_bulan > 0 && it.nilai > 0 && (
                              <div className="md:col-span-12 text-[10px] bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-emerald-800">
                                <TrendingDown className="w-3 h-3 inline mb-0.5 mr-1" />
                                Penyusutan: <b>{idr((it.nilai - it.nilai_residu) / it.umur_manfaat_bulan)}/bln</b>
                                {" "}({(100 / it.umur_manfaat_bulan).toFixed(2)}%/bln) · setelah {it.umur_manfaat_bulan} bln = {idr(it.nilai_residu)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-500">
                    Total nilai: <b className="font-mono text-slate-900">
                      {idr(batchItems.reduce((s, it) => s + (parseFloat(it.nilai) || 0), 0))}
                    </b> · {batchItems.filter(it => (it.nama || "").trim()).length} item terisi
                  </p>
                  <div className="flex gap-2">
                    <button onClick={assetGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                    <button onClick={save} data-testid="btn-save-batch-asset"
                      className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
                      <Layers className="w-4 h-4" /> Simpan {batchItems.filter(it => (it.nama || "").trim()).length} Aset
                    </button>
                  </div>
                </div>
              </div>
            )}
            {assetGuard.confirming && (
              <UnsavedGuardBar onStay={assetGuard.cancelClose} onDiscard={assetGuard.confirmDiscard} testid="asset-modal-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* ===== QR Code Modal ===== */}
      {qrModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={closeQrModal}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="qr-modal">
            <div className="bg-gradient-to-r from-emerald-700 to-emerald-500 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                <div>
                  <h3 className="font-display font-semibold text-sm">Label QR Aset</h3>
                  <p className="text-[11px] text-emerald-100 truncate max-w-[220px]">{qrModal.asset.nama}</p>
                </div>
              </div>
              <button onClick={closeQrModal} className="p-1.5 hover:bg-white/20 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* QR Image via blob URL */}
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-4 flex flex-col items-center">
                {qrModal.blobUrl ? (
                  <img src={qrModal.blobUrl} alt="QR Code" className="w-56 h-56 object-contain"
                    data-testid="qr-preview-img" />
                ) : (
                  <LoadingState loading data-testid="qr-loading"
                    renderSkeleton={() => (
                      <div className="w-56 h-56 grid place-items-center">
                        <Skeleton className="w-48 h-48 rounded-lg" />
                      </div>
                    )} />
                )}
                <p className="mt-2 text-xs font-mono font-bold text-slate-900">
                  {(qrModal.asset.kode || qrModal.asset.id.slice(0, 8)).toUpperCase()}
                </p>
                <p className="text-[11px] text-slate-500">{qrModal.asset.nama}</p>
                {(qrModal.asset.serial_no || qrModal.asset.imei) && (
                  <p className="text-[10px] font-mono text-slate-400">
                    {qrModal.asset.serial_no ? `S/N: ${qrModal.asset.serial_no}` : `IMEI: ${qrModal.asset.imei}`}
                  </p>
                )}
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-[11px] text-emerald-800">
                Scan QR &rarr; langsung buka detail aset ini di HP (nilai, pemegang, riwayat). Butuh login {appName}.
              </div>

              {/* Print options */}
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                <label className="block">
                  <span className="text-[10px] font-medium text-slate-500">Ukuran Label</span>
                  <select value={qrLabelSize} onChange={e => setQrLabelSize(e.target.value)}
                    data-testid="qr-label-size"
                    className="mt-0.5 w-full h-8 px-2 rounded border border-slate-200 text-xs bg-white">
                    <option value="small">Kecil (45mm)</option>
                    <option value="medium">Sedang (60mm)</option>
                    <option value="large">Besar (80mm)</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] font-medium text-slate-500">Kolom/baris</span>
                  <select value={qrColumns} onChange={e => setQrColumns(parseInt(e.target.value))}
                    data-testid="qr-columns"
                    className="mt-0.5 w-full h-8 px-2 rounded border border-slate-200 text-xs bg-white">
                    <option value="1">1 kolom</option>
                    <option value="2">2 kolom</option>
                    <option value="3">3 kolom</option>
                    <option value="4">4 kolom</option>
                  </select>
                </label>
              </div>

              <div className="flex flex-col gap-2 pt-2">
                <button onClick={() => downloadQrPng(qrModal.asset)} data-testid="btn-download-qr-png"
                  className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
                  <Download className="w-4 h-4" /> Unduh QR PNG
                </button>
                <button onClick={() => printBulkQrLabels([qrModal.asset.id])} data-testid="btn-print-single-qr"
                  disabled={qrPrinting}
                  className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                  <Printer className="w-4 h-4" /> {qrPrinting ? "Membuat PDF..." : "Print Label PDF"}
                </button>
                <a href={`/scan/asset/${qrModal.asset.id}`} target="_blank" rel="noopener noreferrer"
                  data-testid="btn-open-scan-page"
                  className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium">
                  <ArrowRight className="w-4 h-4" /> Buka Halaman Scan
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ l, children, className = "" }) {
  return <label className={`flex flex-col gap-1 ${className}`}>
    <span className="text-xs font-medium text-slate-600">{l}</span>{children}
  </label>;
}
function Inp({ v, onC, type = "text", tid }) {
  return <input type={type} value={v} onChange={e => onC(e.target.value)} data-testid={tid}
    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-emerald-600/40" />;
}
function SumCard({ label, value, sub, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5">
      <p className={`text-xs font-semibold uppercase tracking-wider text-${tint}-700 mb-2`}>{label}</p>
      <p className="font-display text-xl md:text-2xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

